import { useReducer, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setOnboardingCompleted } from 'redux_dir/features/newOnboardingSlice';
import { useCompleteOnboardingMutation } from 'redux_dir/features/parentApi';
import { getParentForm, setParentForm } from 'redux_dir/features/parentSlice';
import { toast } from 'sonner';
import {
  STEPS,
  STEP_ORDER,
  STUDENT_STEP_ORDER,
  STEP_CONFIG,
  MAX_GOALS,
} from '../constants/onboardingData';

// ===========================================
// CONSTANTES LOCALES
// ===========================================
const STORAGE_KEY = 'newOnboardingData';

// ===========================================
// ÉTAT INITIAL
// ===========================================
const initialState = {
  step: STEPS.USER_TYPE,
  formData: {
    userType: null,
    // Flow Parent
    childName: '',
    childGender: null,
    childBirthYear: null,
    childClass: null,
    childDifficulties: [],
    availabilitySlots: [],
    dailyDuration: null,
    learningGoals: [],
    // Flow Élève
    studentName: '',
    studentGender: null,
    studentBirthYear: null,
    studentClass: null,
    studentDifficulties: [],
    wantsParentContact: null, // true = 'oui', false = 'non'
    parentPhone: '',
  },
  isCompleted: false,
};

// ===========================================
// REDUCER
// ===========================================
function onboardingReducer(state, action) {
  switch (action.type) {
    case 'SELECT_USER_TYPE':
      // Déterminer la prochaine étape selon le type d'utilisateur
      // Pour élève: aller directement à la classe (le nom est déjà renseigné à l'inscription)
      // Pour parent: aller aux infos de l'enfant
      const nextStep = action.payload === 'eleve' ? STEPS.STUDENT_CLASS : STEPS.CHILD_INFO;
      return {
        ...state,
        step: nextStep,
        formData: {
          ...state.formData,
          userType: action.payload,
        },
      };

    case 'UPDATE_CHILD_INFO':
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.payload.field]: action.payload.value,
        },
      };

    case 'TOGGLE_DIFFICULTY':
      const { childDifficulties } = state.formData;
      const difficultyId = action.payload;
      
      // Si "aucune" est sélectionné, désélectionner les autres
      if (difficultyId === 'aucune') {
        return {
          ...state,
          formData: {
            ...state.formData,
            childDifficulties: childDifficulties.includes('aucune') ? [] : ['aucune'],
          },
        };
      }
      
      // Si on sélectionne autre chose, retirer "aucune"
      const newDifficulties = childDifficulties.includes(difficultyId)
        ? childDifficulties.filter(d => d !== difficultyId)
        : [...childDifficulties.filter(d => d !== 'aucune'), difficultyId];
      
      return {
        ...state,
        formData: {
          ...state.formData,
          childDifficulties: newDifficulties,
        },
      };

    case 'TOGGLE_AVAILABILITY':
      const { availabilitySlots } = state.formData;
      const slotId = action.payload;
      const newSlots = availabilitySlots.includes(slotId)
        ? availabilitySlots.filter(s => s !== slotId)
        : [...availabilitySlots, slotId];
      
      return {
        ...state,
        formData: {
          ...state.formData,
          availabilitySlots: newSlots,
        },
      };

    case 'SELECT_DURATION':
      return {
        ...state,
        formData: {
          ...state.formData,
          dailyDuration: action.payload,
        },
      };

    case 'TOGGLE_GOAL':
      const { learningGoals } = state.formData;
      const goalId = action.payload;
      
      if (learningGoals.includes(goalId)) {
        return {
          ...state,
          formData: {
            ...state.formData,
            learningGoals: learningGoals.filter(g => g !== goalId),
          },
        };
      }
      
      // Ne pas dépasser MAX_GOALS
      if (learningGoals.length >= MAX_GOALS) {
        return state;
      }
      
      return {
        ...state,
        formData: {
          ...state.formData,
          learningGoals: [...learningGoals, goalId],
        },
      };

    case 'NEXT_STEP':
      // Utiliser le bon ordre d'étapes selon le type d'utilisateur
      const stepOrder = state.formData.userType === 'eleve' ? STUDENT_STEP_ORDER : STEP_ORDER;
      const currentIndex = stepOrder.indexOf(state.step);
      const nextStepInOrder = stepOrder[currentIndex + 1] || state.step;
      return {
        ...state,
        step: nextStepInOrder,
      };

    case 'PREV_STEP':
      const prevStepOrder = state.formData.userType === 'eleve' ? STUDENT_STEP_ORDER : STEP_ORDER;
      const prevIndex = prevStepOrder.indexOf(state.step);
      const prevStep = prevStepOrder[Math.max(0, prevIndex - 1)];
      return {
        ...state,
        step: prevStep,
      };

    case 'COMPLETE':
      return {
        ...state,
        step: STEPS.SUCCESS,
        isCompleted: true,
      };

    case 'RESTORE_STATE':
      return {
        ...action.payload,
        isCompleted: false,
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}


// ===========================================
// HELPERS
// ===========================================
const saveToLocalStorage = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    return true;
  } catch (error) {
    console.error('Erreur sauvegarde localStorage:', error);
    return false;
  }
};

const loadFromLocalStorage = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Erreur lecture localStorage:', error);
    return null;
  }
};

const clearLocalStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Erreur suppression localStorage:', error);
  }
};

// ===========================================
// HOOK PRINCIPAL
// ===========================================
export default function useNewOnboarding() {
  const navigate = useNavigate();
  const reduxDispatch = useDispatch();
  const parentForm = useSelector(getParentForm);
  const [completeOnboarding, { isLoading: isCompletingOnboarding }] = useCompleteOnboardingMutation();

  const [state, dispatch] = useReducer(onboardingReducer, initialState);
  const { step, formData, isCompleted } = state;

  // Restaurer l'état depuis localStorage au montage
  useEffect(() => {
    const savedState = loadFromLocalStorage();
    if (savedState && !savedState.isCompleted) {
      dispatch({ type: 'RESTORE_STATE', payload: savedState });
    }
  }, []);

  // Sauvegarder l'état à chaque changement
  useEffect(() => {
    if (step !== STEPS.USER_TYPE && !isCompleted) {
      saveToLocalStorage(state);
    }
  }, [state, step, isCompleted]);

  // ===========================================
  // VALIDATION
  // ===========================================
  const canContinue = useCallback(() => {
    switch (step) {
      case STEPS.USER_TYPE:
        return formData.userType !== null;

      // Flow Parent
      case STEPS.CHILD_INFO:
        return (
          formData.childName?.trim().length > 0 &&
          formData.childGender !== null &&
          formData.childBirthYear !== null &&
          formData.childClass !== null
        );

      case STEPS.AVAILABILITY:
        return formData.availabilitySlots.length > 0;

      case STEPS.DURATION:
        return formData.dailyDuration !== null;

      case STEPS.GOALS:
        return formData.learningGoals.length > 0;

      case STEPS.SOCIAL_PROOF:
        return true;

      // Flow Élève
      case STEPS.STUDENT_NAME:
        return formData.studentName?.trim().length > 0;

      case STEPS.STUDENT_CLASS:
        return formData.studentClass !== null;

      case STEPS.STUDENT_INFO:
        return (
          formData.studentGender !== null &&
          formData.studentBirthYear !== null
        );

      case STEPS.PARENT_CONTACT:
        // Si "oui contactez mon parent" est sélectionné, le numéro est requis
        if (formData.wantsParentContact === true) {
          return formData.parentPhone?.trim().length >= 9;
        }
        // Si "non" ou pas encore choisi
        return formData.wantsParentContact !== null;

      default:
        return false;
    }
  }, [step, formData]);

  const getValidationMessage = useCallback(() => {
    switch (step) {
      // Flow Parent
      case STEPS.CHILD_INFO:
        if (!formData.childName?.trim()) return "Veuillez entrer le prénom de l'enfant";
        if (!formData.childGender) return 'Veuillez sélectionner le genre';
        if (!formData.childBirthYear) return "Veuillez sélectionner l'année de naissance";
        if (!formData.childClass) return 'Veuillez sélectionner la classe';
        return null;

      case STEPS.AVAILABILITY:
        if (formData.availabilitySlots.length === 0) {
          return 'Veuillez sélectionner au moins un créneau';
        }
        return null;

      case STEPS.DURATION:
        if (!formData.dailyDuration) return 'Veuillez sélectionner une durée';
        return null;

      case STEPS.GOALS:
        if (formData.learningGoals.length === 0) {
          return 'Sélectionnez au moins 1 objectif pour continuer';
        }
        return null;

      // Flow Élève
      case STEPS.STUDENT_NAME:
        if (!formData.studentName?.trim()) return 'Veuillez entrer ton prénom';
        return null;

      case STEPS.STUDENT_CLASS:
        if (!formData.studentClass) return 'Veuillez sélectionner ta classe';
        return null;

      case STEPS.STUDENT_INFO:
        if (!formData.studentGender) return 'Veuillez sélectionner ton genre';
        if (!formData.studentBirthYear) return 'Veuillez sélectionner ton année de naissance';
        return null;

      case STEPS.PARENT_CONTACT:
        if (formData.wantsParentContact === null) return 'Veuillez faire un choix';
        if (formData.wantsParentContact && !formData.parentPhone?.trim()) {
          return 'Veuillez entrer le numéro de téléphone de ton parent';
        }
        return null;

      default:
        return null;
    }
  }, [step, formData]);

  // ===========================================
  // HANDLERS
  // ===========================================
  const handleSelectUserType = (type) => {
    dispatch({ type: 'SELECT_USER_TYPE', payload: type });
  };

  const handleUpdateChildInfo = (field, value) => {
    dispatch({ type: 'UPDATE_CHILD_INFO', payload: { field, value } });
  };

  const handleToggleDifficulty = (difficultyId) => {
    dispatch({ type: 'TOGGLE_DIFFICULTY', payload: difficultyId });
  };

  const handleToggleAvailability = (slotId) => {
    dispatch({ type: 'TOGGLE_AVAILABILITY', payload: slotId });
  };

  const handleSelectDuration = (duration) => {
    dispatch({ type: 'SELECT_DURATION', payload: duration });
  };

  const handleToggleGoal = (goalId) => {
    dispatch({ type: 'TOGGLE_GOAL', payload: goalId });
  };

  // Handlers pour le flow élève
  const handleUpdateStudentInfo = (field, value) => {
    dispatch({ type: 'UPDATE_CHILD_INFO', payload: { field, value } });
  };

  const handleToggleStudentDifficulty = (difficultyId) => {
    // Réutiliser la même logique mais pour studentDifficulties
    const { studentDifficulties } = formData;
    let newDifficulties;
    
    if (difficultyId === 'aucune') {
      newDifficulties = studentDifficulties.includes('aucune') ? [] : ['aucune'];
    } else {
      newDifficulties = studentDifficulties.includes(difficultyId)
        ? studentDifficulties.filter(d => d !== difficultyId)
        : [...studentDifficulties.filter(d => d !== 'aucune'), difficultyId];
    }
    
    dispatch({ type: 'UPDATE_CHILD_INFO', payload: { field: 'studentDifficulties', value: newDifficulties } });
  };

  const handleSetParentContact = (wantsContact) => {
    dispatch({ type: 'UPDATE_CHILD_INFO', payload: { field: 'wantsParentContact', value: wantsContact } });
  };

  const handleContinue = () => {
    if (canContinue()) {
      dispatch({ type: 'NEXT_STEP' });
    }
  };

  const handleBack = () => {
    dispatch({ type: 'PREV_STEP' });
  };

  const handleComplete = async () => {
    // Vérifier qu'on a un parent connecté
    if (!parentForm?.id) {
      toast.error("Erreur: Vous devez être connecté pour continuer");
      navigate('/parentProspect', { replace: true });
      return;
    }

    try {
      // Préparer les données selon le type d'utilisateur
      let payload = {
        parentId: parentForm.id,
        userType: formData.userType,
      };

      if (formData.userType === 'eleve') {
        // Flow élève - utiliser le nom du parent (fullName) car c'est l'élève lui-même
        payload = {
          ...payload,
          childName: parentForm.fullName, // Utiliser le nom renseigné à l'inscription
          childGender: formData.studentGender,
          childBirthYear: formData.studentBirthYear,
          childClass: formData.studentClass,
          childDifficulties: formData.studentDifficulties,
          wantsParentContact: formData.wantsParentContact,
          parentPhone: formData.parentPhone,
        };
      } else {
        // Flow parent
        payload = {
          ...payload,
          childName: formData.childName,
          childGender: formData.childGender,
          childBirthYear: formData.childBirthYear,
          childClass: formData.childClass,
          childDifficulties: formData.childDifficulties,
          availabilitySlots: formData.availabilitySlots,
          dailyDuration: formData.dailyDuration,
          learningGoals: formData.learningGoals,
        };
      }

      // Appeler l'API pour compléter l'onboarding et créer l'enfant
      const result = await completeOnboarding(payload).unwrap();

      // Mettre à jour le parent dans Redux avec has_completed_onboarding = true
      reduxDispatch(setParentForm({
        ...parentForm,
        has_completed_onboarding: true,
        ...result?.data?.parent,
      }));

      // Marquer l'onboarding comme complété
      dispatch({ type: 'COMPLETE' });
      reduxDispatch(setOnboardingCompleted(formData));
      
      // Nettoyer localStorage
      clearLocalStorage();

      toast.success("Profil créé avec succès !");
    } catch (error) {
      console.error("Erreur lors de la complétion de l'onboarding:", error);
      toast.error(error?.data?.message || "Une erreur est survenue");
    }
  };

  const handleNavigateToAction = (route) => {
    navigate(route, { replace: true });
  };

  const handleReset = () => {
    dispatch({ type: 'RESET' });
    clearLocalStorage();
  };

  // ===========================================
  // COMPUTED VALUES
  // ===========================================
  const stepConfig = STEP_CONFIG[step] || {};
  const currentStepNumber = stepConfig.number || null;
  const currentStepMessage = stepConfig.message || '';
  const showProgressBar = stepConfig.showProgress ?? true;
  const totalSteps = stepConfig.totalSteps || 6;
  
  const currentStepOrder = formData.userType === 'eleve' ? STUDENT_STEP_ORDER : STEP_ORDER;
  const canGoBack = currentStepOrder.indexOf(step) > 0 && step !== STEPS.SUCCESS;

  // ===========================================
  // RETOUR DU HOOK
  // ===========================================
  return {
    // State
    step,
    formData,
    isCompleted,
    isCompletingOnboarding,
    
    // Computed
    currentStepNumber,
    currentStepMessage,
    showProgressBar,
    totalSteps,
    canGoBack,
    canContinue: canContinue(),
    validationMessage: getValidationMessage(),
    
    // Handlers
    handleSelectUserType,
    handleUpdateChildInfo,
    handleToggleDifficulty,
    handleToggleAvailability,
    handleSelectDuration,
    handleToggleGoal,
    // Handlers flow élève
    handleUpdateStudentInfo,
    handleToggleStudentDifficulty,
    handleSetParentContact,
    // Handlers communs
    handleContinue,
    handleBack,
    handleComplete,
    handleNavigateToAction,
    handleReset,
  };
}
